import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Media } from "@/components/media/types";

interface OptimizedMemorialData {
  memorial: Record<string, unknown> | null;
  media: Media[];
  featuredPhotos: Record<string, unknown>[];
  stories: Record<string, unknown>[];
  familyMembers: Record<string, unknown>[];
  interests: Record<string, unknown>[];
  lifeStages: Record<string, unknown>[];
}

export const useOptimizedMemorialData = (memorialId: string | undefined) => {
  return useQuery({
    queryKey: ['optimized-memorial-data', memorialId],
    queryFn: async (): Promise<OptimizedMemorialData> => {
      if (!memorialId) throw new Error('Memorial ID is required');

      // Fetch all data in parallel for better performance
      const [
        memorialResponse,
        mediaResponse,
        featuredPhotosResponse,
        storiesResponse,
        familyMembersResponse,
        interestsResponse,
        lifeStagesResponse
      ] = await Promise.all([
        supabase
          .from('memorials_public')
          .select('*')
          .eq('id', memorialId)
          .single(),
        supabase
          .from('media')
          .select('*')
          .eq('memorial_id', memorialId)
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false }),
        supabase
          .from('featured_photos')
          .select('*')
          .eq('memorial_id', memorialId)
          .order('order_index', { ascending: true }),
        supabase
          .from('stories')
          .select('*')
          .eq('memorial_id', memorialId)
          .order('created_at', { ascending: false }),
        supabase
          .from('family_members')
          .select('*')
          .eq('memorial_id', memorialId),
        supabase
          .from('interests_and_personality')
          .select('*')
          .eq('memorial_id', memorialId)
          .maybeSingle(),
        supabase
          .from('memorial_life_stages')
          .select('*, life_stages_reference(*)')
          .eq('memorial_id', memorialId)
          .order('created_at', { ascending: true })
      ]);

      if (memorialResponse.error) throw memorialResponse.error;
      if (mediaResponse.error) throw mediaResponse.error;
      if (featuredPhotosResponse.error) throw featuredPhotosResponse.error;
      if (storiesResponse.error) throw storiesResponse.error;
      if (familyMembersResponse.error) throw familyMembersResponse.error;
      if (interestsResponse.error) throw interestsResponse.error;
      if (lifeStagesResponse.error) throw lifeStagesResponse.error;

      // Smart URL processing for media URLs
      const mediaData = mediaResponse.data || [];
      
      // Process URLs - handle both full URLs and storage paths
      const mediaWithUrls = mediaData.map((item) => {
        // Check if URL is already a full public URL
        const processedUrl = item.url.startsWith('https://') 
          ? item.url 
          : supabase.storage.from('memorials').getPublicUrl(item.url).data.publicUrl;
        
        // Same logic for thumbnail URLs
        const processedThumbnailUrl = item.thumbnail_url 
          ? (item.thumbnail_url.startsWith('https://') 
              ? item.thumbnail_url 
              : supabase.storage.from('memorials').getPublicUrl(item.thumbnail_url).data.publicUrl)
          : null;
        
        return {
          ...item,
          url: processedUrl,
          thumbnail_url: processedThumbnailUrl
        } as Media;
      });

      // Batch process featured photos URLs
      const featuredPhotosData = featuredPhotosResponse.data || [];
      const featuredPhotosWithUrls = featuredPhotosData.map(item => ({
        ...item,
        url: supabase.storage.from('memorials').getPublicUrl(item.url).data.publicUrl
      }));

      return {
        memorial: memorialResponse.data,
        media: mediaWithUrls,
        featuredPhotos: featuredPhotosWithUrls,
        stories: storiesResponse.data || [],
        familyMembers: familyMembersResponse.data || [],
        interests: interestsResponse.data ? [interestsResponse.data] : [],
        lifeStages: lifeStagesResponse.data || []
      };
    },
    enabled: !!memorialId,
    staleTime: 0, // Always refetch to ensure theme changes are visible immediately
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: 'always', // Always refetch when component mounts
  });
};